import React from "react";
import PlaceSearch from "./PlaceSearch";

// Birth data in, query string out. A place is searched for by name and fills in
// latitude, longitude AND the IANA zone — the zone being the part that decides
// whether a 1985 chart is an hour wrong.
export default function ChartForm({ form, timezones, houseSystems, deferred, orbSets, onChange }) {
  // Editing a coordinate or zone by hand means the city no longer describes the
  // form, so the chosen place is released rather than left claiming it does.
  const set = (key) => (event) =>
    onChange({ ...form, [key]: event.target.value, place: null, placeLabel: null });

  const choosePlace = (place) =>
    onChange({
      ...form,
      place: String(place.id),
      placeLabel: place.label,
      latitude: String(place.latitude),
      longitude: String(place.longitude),
      timezone: place.timezone
    });

  const clearPlace = () => onChange({ ...form, place: null, placeLabel: null });

  return (
    <form className="chartform" onSubmit={(event) => event.preventDefault()} data-testid="chart-form">
      <div className="chartform__row">
        <label className="chartform__field">
          <span>Date of birth</span>
          <input type="date" value={form.date} onChange={set("date")} data-testid="field-date" />
        </label>
        <label className="chartform__field">
          <span>Time of birth</span>
          <input type="time" value={form.time} onChange={set("time")} data-testid="field-time" />
        </label>
      </div>

      <PlaceSearch label={form.placeLabel} onChoose={choosePlace} onClear={clearPlace} />

      <div className="chartform__row">
        <label className="chartform__field">
          <span>Latitude</span>
          <input inputMode="decimal" value={form.latitude} onChange={set("latitude")}
                 data-testid="field-latitude" />
          <small>Decimal degrees. North positive.</small>
        </label>
        <label className="chartform__field">
          <span>Longitude</span>
          <input inputMode="decimal" value={form.longitude} onChange={set("longitude")}
                 data-testid="field-longitude" />
          <small>Decimal degrees. <strong>East positive</strong>, so the Americas are negative.</small>
        </label>
      </div>

      <label className="chartform__field">
        <span>Time zone</span>
        <select value={form.timezone} onChange={set("timezone")} data-testid="field-timezone">
          {timezones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
        </select>
        <small>
          The zone, not an offset — the offset for a place changes with the date,
          and that is what makes old charts wrong.
        </small>
      </label>

      <div className="chartform__row">
        <label className="chartform__field">
          <span>House system</span>
          <select value={form.houseSystem} onChange={set("houseSystem")} data-testid="field-houses">
            {houseSystems.map((system) => (
              <option key={system.slug} value={system.slug}>{system.name}</option>
            ))}
          </select>
          {deferred.length > 0 && (
            <small data-testid="deferred-houses">
              {deferred.map((system) => system.name).join(", ")} need an ephemeris and
              are not offered rather than being approximated.
            </small>
          )}
          {deferred.length === 0 && (
            <small data-testid="ephemeris-houses">
              Placidus, Koch, Regiomontanus and Campanus are calculated by Swiss Ephemeris.
            </small>
          )}
        </label>

        <label className="chartform__field">
          <span>Orbs</span>
          <select value={form.orbSet} onChange={set("orbSet")} data-testid="field-orbs">
            {orbSets.map((set_) => <option key={set_.slug} value={set_.slug}>{set_.name}</option>)}
          </select>
          <small>{orbSets.find((s) => s.slug === form.orbSet)?.note}</small>
        </label>
      </div>
    </form>
  );
}
